// "The Filing" — the warm-up splash that sits between Sign in and the
// dashboard. Same document world as LoginFiling.tsx: warm paper, the
// "code clear" masthead, a progress meter, five step rows that resolve
// spinner -> teal check, then a neutral READY affordance.
//
// Pixel-port of the Claude Design handoff (carbon-clear/project/login-
// loading.jsx, component LoadingFiling) with two CTO-mandated changes:
//
//   1. The design's synthetic ``setProgress`` interval engine (an
//      incrementing setTimeout that FABRICATES advancement) is DELETED.
//      Percent + each step's state + every label/detail come entirely
//      from ``useWarmupStatusQuery`` — real snapshot freshness, BE-side.
//      There is NO client-side timer driving progress anywhere here.
//
//   2. The design's "CLEARED" compliance seal is renamed to a neutral
//      "READY" workspace affordance. We NEVER stamp a compliance verdict
//      or render a $ exposure / deadline number over cold/partial/failed
//      data — the exp/due step details arrive as ``null`` from the
//      payload until those source groups are actually fresh, and we
//      render only what the payload sends.
//
// Tracker-ONLY, fail-toward-no-splash: a non-Tracker (Classic/filing)
// user is bounced to /buildings before any warm-up theater. NEVER block:
// a client max-wait guard + an always-available "Enter dashboard ->"
// escape guarantee a stalled/dead warm-up worker can't trap a login.
//
// Token/font constants are DUPLICATED locally (not imported from
// LoginFiling) to avoid a cross-stream file conflict during parallel
// development; a later cleanup extracts the shared set.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";

import { useMeQuery, useWarmupStatusQuery } from "@/queries/queries";
import { prefetchPortalDashboard, usePortalBuildingsQuery } from "@/queries/portalQueries";
import type { WarmupStep, WarmupStepState } from "@/api/portal";
import { redirectTo } from "@/lib/postLoginHome";

const routeApi = getRouteApi("/welcome");

// Palette — bridged to CSS vars, not hand-rolled hex (mirrors LoginFiling;
// see feedback_tailwind_tokens_via_cssvars_only). Duplicated on purpose.
const INK = "hsl(var(--ink))";
const MUTE = "hsl(var(--ink-mute))";
const FAINT = "hsl(var(--login-faint))";
const LINE = "hsl(var(--ink-line))";
const PAPER = "hsl(var(--login-paper))";
const TEAL = "hsl(var(--teal))";
const DANGER = "hsl(var(--destructive))";

const SERIF = "'Newsreader', Georgia, serif";
const SANS = "'Inter', system-ui, sans-serif";
const MONO = "'JetBrains Mono', ui-monospace, monospace";

// Client-side wall-clock guard. The BE has its own ~10s max-wait; this is
// a belt-and-suspenders cap so a hung worker / dead poll can never trap a
// login. On fire we stop polling and show the escape. ~30s wall-clock.
const CLIENT_MAX_WAIT_MS = 30_000;
// Deliberate splash: ALWAYS dwell here at least this long, even for a
// fully-warm portfolio (product spec 2026-06-05). The dwell is not dead
// time — we prefetch the whole dashboard during it (see prefetch effect)
// so the hand-off to /portal renders from cache in <100ms. There is no
// early-skip in the happy path (the spec says *at least* 5s); the
// "Enter dashboard" button is reserved for the failure/timeout escape.
const MIN_DWELL_MS = 5_000;
// Once the seal is shown, keep it visible at least this long even if ready
// arrived AFTER the min dwell (slow cold start), so the stamp never flashes.
const SEAL_MIN_VISIBLE_MS = 900;

// Human-readable label per step id. The payload owns the *detail* (the
// mono right-hand value, e.g. the user email or "13 buildings"); the FE
// owns only the static left-hand label copy.
const STEP_LABELS: Record<string, string> = {
  auth: "Identity verified",
  bld: "Loading your portfolio",
  rec: "Pulling public records",
  exp: "Computing exposure",
  due: "Checking deadlines",
};

const STEP_ORDER = ["auth", "bld", "rec", "exp", "due"] as const;

function WarmupLogo({ color = INK }: { color?: string }) {
  // Official CodeClear cube mark (filled green top face + ink strokes) —
  // matches /codeClearLogo.svg and the landing-page mark exactly. Wordmark
  // is "CodeClear" (one word, title-case), not "code clear".
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <svg width="24" height="24" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <path d="M16 4 L27 10.5 L16 17 L5 10.5 Z" fill="#1A8A60" />
        <path d="M16 4 L27 10.5 L27 22 L16 28.5 L5 22 L5 10.5 Z" fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        <path d="M5 10.5 L16 17 L27 10.5 M16 17 L16 28.5" fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        <path d="M16 20.8 L27 14.3 M16 24.7 L27 18.2 M5 14.3 L16 20.8 M5 18.2 L16 24.7" fill="none" stroke={color} strokeWidth="1.1" strokeLinecap="round" opacity="0.5" />
      </svg>
      <span style={{ fontSize: 17, fontWeight: 700, letterSpacing: -0.4, color }}>
        CodeClear
      </span>
    </div>
  );
}

function StepRow({ label, state, detail }: { label: string; state: WarmupStepState; detail?: string | null }) {
  const dimmed = state === "pending";
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "24px 1fr auto",
        gap: 16,
        alignItems: "center",
        padding: "15px 0",
        borderBottom: `1px solid ${LINE}`,
        opacity: dimmed ? 0.4 : 1,
        transition: "opacity 300ms",
      }}
    >
      <div style={{ display: "flex", justifyContent: "center" }}>
        {state === "running" && (
          <span
            aria-hidden="true"
            style={{
              width: 13,
              height: 13,
              borderRadius: 999,
              border: `2px solid ${LINE}`,
              borderTopColor: TEAL,
              display: "inline-block",
              animation: "ldspin 0.8s linear infinite",
            }}
          />
        )}
        {state === "done" && (
          <span
            aria-hidden="true"
            style={{
              width: 15,
              height: 15,
              borderRadius: 999,
              background: TEAL,
              color: "white",
              fontSize: 9,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✓
          </span>
        )}
        {state === "error" && (
          <span
            aria-hidden="true"
            style={{
              width: 9,
              height: 9,
              borderRadius: 999,
              background: DANGER,
              display: "inline-block",
            }}
          />
        )}
        {state === "pending" && (
          <span
            aria-hidden="true"
            style={{
              width: 7,
              height: 7,
              borderRadius: 999,
              background: LINE,
              display: "inline-block",
            }}
          />
        )}
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 500,
          color: state === "error" ? DANGER : dimmed ? MUTE : INK,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: MONO,
          fontSize: 11,
          letterSpacing: 0.3,
          color: state === "error" ? DANGER : state === "done" ? INK : FAINT,
        }}
      >
        {/* Detail is rendered ONLY from the payload. exp/due arrive as
            null until fresh — we never invent a $ figure or a deadline.
            A `…` placeholder marks an in-flight step with no detail yet. */}
        {detail ? detail : state === "running" ? "…" : ""}
      </div>
    </div>
  );
}

type ExitReason = "auto" | "button" | "timeout" | "fail" | "guard";

const WelcomeWarmup = () => {
  const navigate = useNavigate();
  const search = routeApi.useSearch() as { redirect?: string };
  const meQuery = useMeQuery();
  const warmup = useWarmupStatusQuery();
  // 2026-06-05 — bld row is anchored to the real ``portal/buildings``
  // fetch (the same query the prefetch fires under one batch). Subscribed
  // here so the row's pending→running→done transition is driven by an
  // observable network event — not the BE's hardcoded ``state="done"``
  // on every warmup poll, which read as theater. See renderedSteps below.
  const buildingsQuery = usePortalBuildingsQuery({});

  // Single source of "already left" truth so auto-advance / button /
  // timeout / fail / guard can't double-navigate.
  const exitedRef = useRef(false);
  const enterButtonRef = useRef<HTMLButtonElement | null>(null);
  const queryClient = useQueryClient();
  // Wall-clock anchor for the minimum-dwell gate (product spec: ≥5s here).
  const mountedAtRef = useRef<number>(Date.now());
  const prefetchedRef = useRef(false);

  // Did we EVER show the warming state? Distinguishes the power-user
  // "first poll already ready -> skip the theater" path from the
  // "became ready after warming -> run the seal" path.
  const [showedWarming, setShowedWarming] = useState(false);
  // Cold->ready: hold on the seal until the user enters (or auto-advance).
  const [held, setHeld] = useState(false);
  const [stamped, setStamped] = useState(false);
  // Client wall-clock guard tripped.
  const [maxWaitTripped, setMaxWaitTripped] = useState(false);
  // 2026-06-05 — dashboard prefetch resolution gate. The original code
  // fired ``prefetchPortalDashboard`` and discarded the Promise, so the
  // exit timer could fire while the dashboard query was still in flight
  // and the user would land on /portal staring at a skeleton — defeating
  // the splash. We now track the Promise and gate the exit on it.
  const [prefetchDone, setPrefetchDone] = useState(false);

  const data = warmup.data;
  const me = meQuery.data;
  const firstName: string | undefined = me?.firstName;
  const email: string | undefined = me?.email;

  // --- 2026-06-05 — FE-owned step states for ``auth`` and ``bld``.
  // Before this, the BE hard-coded both to ``done`` on every poll, which
  // landed both rows in the user's first paint with no observable work
  // behind them — the "fake verification" feel. Now each row is anchored
  // to a real, distinct FE network event so the user can SEE a real
  // backend round-trip resolve it:
  //
  //   * auth ← ``useMeQuery`` (the /api/me round-trip)
  //   * bld  ← ``usePortalBuildingsQuery`` (the portfolio fetch)
  //
  // rec / exp / due remain driven by the BE warmup payload because they
  // genuinely depend on snapshot-freshness state on the server.
  const authState: WarmupStepState = meQuery.isError
    ? "error"
    : meQuery.isSuccess
      ? "done"
      : "running";
  const bldState: WarmupStepState = buildingsQuery.isError
    ? "error"
    : buildingsQuery.isSuccess
      ? "done"
      : "running";

  // 2026-06-05 — stricter "real ready" signal. The BE's ``data.ready``
  // latches true on EITHER ``rec_ratio >= 1.0`` OR a 10s server-side
  // max-wait blown (see portal_views.py:1379). On a cold first-login
  // portfolio the second branch fires while snapshots are still
  // warming, the splash auto-exits, and the dashboard renders empty.
  // ``allStepsDone`` requires every step (incl. exp/due group freshness)
  // to be ``done``, which means the dashboard the user lands on will
  // have real $ exposure + deadline numbers, not skeletons.
  //
  // Derived from ``renderedSteps`` (built below) so the FE-owned auth/bld
  // overrides participate — a /me or buildings-list failure that doesn't
  // appear in the BE payload still gates the auto-advance correctly.
  const allStepsDone = useMemo(() => {
    const steps = data?.steps ?? [];
    if (steps.length !== STEP_ORDER.length) return false;
    // rec/exp/due come from the BE payload; auth/bld are FE-owned.
    const beById = new Map(steps.map((s) => [s.id, s.state] as const));
    return STEP_ORDER.every((id) => {
      if (id === "auth") return authState === "done";
      if (id === "bld") return bldState === "done";
      return beById.get(id) === "done";
    });
  }, [data, authState, bldState]);

  // A step explicitly errored OR the query exhausted its retries.
  // Derived from the same merged state ``allStepsDone`` uses so a /me or
  // buildings failure surfaces the escape, not just a BE step error.
  const stepErrored = useMemo(() => {
    if (authState === "error" || bldState === "error") return true;
    return (data?.steps ?? []).some(
      (s) => (s.id === "rec" || s.id === "exp" || s.id === "due") && s.state === "error",
    );
  }, [data, authState, bldState]);
  const failed = warmup.isError || stepErrored;

  // BE's server-side max-wait flag — see portal_views.py:1372. Distinct
  // from ``maxWaitTripped`` (the client-side 30s guard). When the BE
  // budget blows but ``allStepsDone`` hasn't, we surface the "Enter
  // dashboard" escape so the user controls when to leave instead of
  // being auto-dropped on cold data.
  const beMaxWaitExceeded = Boolean(data?.maxWaitExceeded);

  const exit = (reason: ExitReason) => {
    if (exitedRef.current) return;
    exitedRef.current = true;
    navigate(redirectTo(search?.redirect, reason === "guard" ? "/buildings" : "/portal"));
  };

  // --- Non-Tracker guard (belt-and-suspenders; Stream D owns the route).
  // Fail-toward-no-splash: a Classic/filing user must NEVER see the splash
  // or trigger a warm-up. Bounce to /buildings (or redirect) the moment
  // /me resolves and platform_home isn't "portal".
  useEffect(() => {
    if (exitedRef.current) return;
    if (!meQuery.isSuccess) return;
    if (me?.company?.platformHome !== "portal") {
      exit("guard");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meQuery.isSuccess, me?.company?.platformHome]);

  const isTracker = me?.company?.platformHome === "portal";

  // --- Prefetch the whole dashboard during the dwell so the hand-off to
  // /portal renders from cache in <100ms (product spec 2026-06-05). Fire
  // once, Tracker-only, StrictMode-double-mount-guarded.
  //
  // 2026-06-05 fix: track the Promise so the exit timer can gate on
  // resolution. Best-effort — a rejected/empty prefetch still flips the
  // gate so a BE blip never traps the user on the splash.
  useEffect(() => {
    if (!isTracker || prefetchedRef.current) return;
    prefetchedRef.current = true;
    prefetchPortalDashboard(queryClient)
      .then(() => setPrefetchDone(true))
      .catch(() => setPrefetchDone(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTracker]);

  // --- Ready state machine. 2026-06-05: gate on ``allStepsDone`` rather
  // than ``data.ready`` so the splash dwells until exposure + deadline
  // groups are fresh too — the BE's ``data.ready`` is identity-sources-
  // only and latches on a 10s budget blow, both of which can fire over
  // a dashboard that would render with skeletons.
  useEffect(() => {
    if (!data || !isTracker || held) return;

    if (allStepsDone) {
      setHeld(true);
      return;
    }

    // Still warming — remember we showed the theater (drives copy).
    if (!showedWarming) setShowedWarming(true);
    // eslint-disable-next-line consistent-return
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isTracker, allStepsDone, showedWarming, held]);

  // --- Seal animation. Fires the moment we held — gives the seal time
  // to animate in while we wait for the dashboard prefetch to resolve
  // (a slow prefetch must not delay the stamp's appearance).
  useEffect(() => {
    if (!held) return;
    const tStamp = setTimeout(() => setStamped(true), 280);
    return () => clearTimeout(tStamp);
  }, [held]);

  // --- Min-dwell-gated auto-advance. 2026-06-05: also gated on
  // ``prefetchDone`` so the exit never fires while the dashboard query
  // is still in flight. With prefetch and warmup both resolved, exit at
  // the LATER of the 5s minimum dwell or a short seal-visible floor.
  useEffect(() => {
    if (!held || !prefetchDone) return;
    const remaining = Math.max(
      MIN_DWELL_MS - (Date.now() - mountedAtRef.current),
      SEAL_MIN_VISIBLE_MS,
    );
    const tIdle = setTimeout(() => exit("auto"), remaining);
    return () => clearTimeout(tIdle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [held, prefetchDone]);

  // --- Client wall-clock max-wait guard. 2026-06-05: gates on
  // ``allStepsDone`` (matches the new held gate) so a portfolio whose
  // exp/due groups never fully warm in 30s still gets the escape.
  useEffect(() => {
    if (!isTracker) return;
    if (allStepsDone) return;
    const t = setTimeout(() => setMaxWaitTripped(true), CLIENT_MAX_WAIT_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTracker, allStepsDone]);

  // --- Focus the Enter button the moment it appears (a11y).
  // The button is the FAILURE/TIMEOUT/BE-budget-blown escape only — the
  // happy path auto-advances after the ≥5s dwell, with no early-skip
  // ("at least 5s"). 2026-06-05: also surfaced when the BE reports
  // ``max_wait_exceeded`` while ``allStepsDone`` is still false, so a
  // user staring at a still-warming portfolio always has a way out.
  const escapeShown = failed || maxWaitTripped || (beMaxWaitExceeded && !allStepsDone);
  useEffect(() => {
    if (escapeShown) {
      // rAF so the node is mounted before we focus it.
      const id = requestAnimationFrame(() => enterButtonRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    // eslint-disable-next-line consistent-return
    return undefined;
  }, [escapeShown]);

  // Build the rendered step rows. Labels are FE-owned; ``rec``/``exp``/
  // ``due`` states + details come from the BE payload; ``auth`` and
  // ``bld`` states come from the local FE queries so each row's
  // pending→running→done transition is anchored to a real, observable
  // network event (see authState / bldState above). Detail is suppressed
  // for an FE-owned row whose state hasn't reached "done" yet, so the
  // row shows the "…" placeholder rather than a value over an unverified
  // claim (e.g. the BE's hardcoded ``"23 buildings"`` while the local
  // ``buildingsQuery`` is still in flight).
  const renderedSteps: Array<{ id: string; label: string; state: WarmupStepState; detail?: string | null }> =
    useMemo(() => {
      const byId = new Map<string, WarmupStep>();
      for (const s of data?.steps ?? []) byId.set(s.id, s);
      return STEP_ORDER.map((id) => {
        const s = byId.get(id);
        if (id === "auth") {
          return {
            id,
            label: STEP_LABELS[id] ?? id,
            state: authState,
            // /me supplies the email; we keep BE detail as a fallback
            // only when we've actually reached "done".
            detail: authState === "done" ? s?.detail ?? null : null,
          };
        }
        if (id === "bld") {
          return {
            id,
            label: STEP_LABELS[id] ?? id,
            state: bldState,
            detail: bldState === "done" ? s?.detail ?? null : null,
          };
        }
        return {
          id,
          label: STEP_LABELS[id] ?? id,
          state: (s?.state ?? "pending") as WarmupStepState,
          detail: s?.detail ?? null,
        };
      });
    }, [data, authState, bldState]);

  const percent = data?.percent ?? 0;
  const ready = Boolean(held);

  // Headline / eyebrow / formline copy by phase. 2026-06-05: BE-side
  // ``max_wait_exceeded`` also triggers the "slow" phase so the user
  // gets the same "Still working" affordance when the server budget
  // blows mid-warmup as they do when the client wall-clock trips.
  const phase: "warming" | "ready" | "slow" | "failed" = failed
    ? "failed"
    : ready
      ? "ready"
      : (maxWaitTripped || (beMaxWaitExceeded && !allStepsDone))
        ? "slow"
        : "warming";

  const eyebrow =
    phase === "ready"
      ? "Workspace ready"
      : phase === "failed"
        ? "Heads up"
        : phase === "slow"
          ? "Still working"
          : "Preparing your workspace";

  const headline =
    phase === "ready"
      ? "Everything's ready."
      : phase === "failed"
        ? "We'll finish in the background."
        : phase === "slow"
          ? "Taking longer than usual."
          : firstName
            ? `One moment, ${firstName}.`
            : "One moment.";

  const subcopy =
    phase === "ready"
      ? "Opening your portfolio dashboard…"
      : phase === "failed"
        ? "Your dashboard is ready and will keep itself up to date — head in whenever you like."
        : phase === "slow"
          ? "Your dashboard is ready. We'll keep refreshing the latest city records in the background."
          : "We're assembling your portfolio from the latest city records.";


  // While the guard is deciding (or bouncing a non-Tracker), render
  // nothing — no splash for Classic users, no flash before the redirect.
  if (meQuery.isSuccess && !isTracker) return null;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: PAPER,
        fontFamily: SANS,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 48,
      }}
    >
      <style>{`
        @keyframes ldspin { to { transform: rotate(360deg); } }
        @keyframes ldstamp {
          0% { transform: scale(1.6) rotate(-14deg); opacity: 0; }
          60% { transform: scale(0.92) rotate(-8deg); opacity: 1; }
          100% { transform: scale(1) rotate(-8deg); opacity: 1; }
        }
        @keyframes ldfade { from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: none; } }
      `}</style>

      <div style={{ width: 560, maxWidth: "100%" }}>
        <div
          role="status"
          aria-live="polite"
          style={{
            background: "white",
            position: "relative",
            boxShadow:
              "0 1px 0 rgba(0,0,0,0.04), 0 40px 80px -30px rgba(20,20,20,0.18)",
          }}
        >
          {/* masthead */}
          <div
            style={{
              borderBottom: `2px solid ${INK}`,
              padding: "28px 48px 20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <WarmupLogo />
          </div>

          <div style={{ padding: "32px 48px 40px" }}>
            <div
              style={{
                fontFamily: MONO,
                fontSize: 10,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: phase === "failed" ? DANGER : TEAL,
                marginBottom: 12,
              }}
            >
              {eyebrow}
            </div>
            <h1
              style={{
                fontFamily: SERIF,
                fontWeight: 400,
                fontSize: 34,
                letterSpacing: -0.8,
                margin: "0 0 6px",
                lineHeight: 1.1,
                color: INK,
              }}
            >
              {headline}
            </h1>
            <p style={{ fontSize: 13, color: MUTE, margin: "0 0 26px", lineHeight: 1.6 }}>
              {subcopy}
            </p>

            {/* progress meter — value comes ONLY from the payload percent */}
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
              <div
                role="progressbar"
                aria-valuenow={percent}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Workspace preparation progress"
                style={{
                  flex: 1,
                  height: 4,
                  borderRadius: 2,
                  background: LINE,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${percent}%`,
                    background: phase === "failed" ? DANGER : TEAL,
                    borderRadius: 2,
                    transition: "width 420ms cubic-bezier(.2,.7,.3,1)",
                  }}
                />
              </div>
              <div
                style={{
                  fontFamily: MONO,
                  fontSize: 11,
                  color: MUTE,
                  letterSpacing: 0.5,
                  width: 36,
                  textAlign: "right",
                }}
              >
                {percent}%
              </div>
            </div>

            {/* steps */}
            <div style={{ borderTop: `1px solid ${LINE}` }}>
              {renderedSteps.map((s) => {
                // Surface the signed-in email on the auth row when the
                // payload didn't carry it (BE detail is authoritative,
                // but /me is the natural fallback for the identity row).
                const detail =
                  s.id === "auth" && !s.detail ? email ?? null : s.detail;
                return (
                  <StepRow key={s.id} label={s.label} state={s.state} detail={detail} />
                );
              })}
            </div>

            {escapeShown && (
              <button
                ref={enterButtonRef}
                type="button"
                onClick={() => exit("button")}
                style={{
                  all: "unset",
                  marginTop: 26,
                  boxSizing: "border-box",
                  width: "100%",
                  height: 52,
                  background: INK,
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: "pointer",
                  animation: "ldfade 360ms ease both",
                }}
              >
                Enter dashboard <span aria-hidden="true">→</span>
              </button>
            )}
          </div>

          {/* the seal — neutral READY affordance, NOT a compliance verdict.
              Only appears on a cold->ready hold. */}
          {stamped && (
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                top: 96,
                right: 52,
                width: 96,
                height: 96,
                borderRadius: 999,
                border: `2.5px solid ${TEAL}`,
                color: TEAL,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                animation: "ldstamp 420ms cubic-bezier(.2,.8,.3,1) both",
                background: "rgba(232,243,241,0.55)",
              }}
            >
              <div
                style={{
                  fontFamily: SERIF,
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: 1,
                  marginBottom: 2,
                }}
              >
                READY
              </div>
              <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
                <path d="M16 4 L27 10.5 L16 17 L5 10.5 Z" fill={TEAL} opacity="0.18" />
                <path d="M16 4 L27 10.5 L27 22 L16 28.5 L5 22 L5 10.5 Z" fill="none" stroke={TEAL} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
                <path d="M5 10.5 L16 17 L27 10.5 M16 17 L16 28.5" fill="none" stroke={TEAL} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
              </svg>
            </div>
          )}
        </div>

        <div
          style={{
            textAlign: "center",
            marginTop: 22,
            fontFamily: MONO,
            fontSize: 9,
            letterSpacing: 1,
            textTransform: "uppercase",
            color: FAINT,
          }}
        >
          {ready
            ? firstName
              ? `Welcome back, ${firstName}`
              : "Welcome back"
            : "Preparing…"}
        </div>
      </div>
    </div>
  );
};

export default WelcomeWarmup;
